import { useMutation } from '@apollo/client'
import { Alert, Button, Descriptions, Form, FormFeedback } from 'admin-web-ui'
import gql from 'graphql-tag'
import { Store } from 'rc-field-form/lib/interface'
import { Link, useNavigate } from 'react-router-dom'
import React, { useMemo } from 'react'
import { useAsyncFn } from 'react-use'
import styled from 'styled-components'
import { Project, User, UserStatus } from '../../../api'
import { useConsoleContext } from '../../../containers/Console'
import { UserFormFieldData, UserFormFields } from '../UserFormFields'
import { ResendInvitationButton } from './ResendInvitationButton'

const UPDATE_USER = gql`
  mutation updateUser(
    $username: ID!
    $firstName: String
    $lastName: String
    $groups: [String!]!
  ) {
    updateUser(
      username: $username
      firstName: $firstName
      lastName: $lastName
      groups: $groups
    )
  }
`

const AccountInfoDescriptions = styled(Descriptions)`
  padding: 10px;
  margin-bottom: 30px;
`

const DescriptionActions = styled.div`
  float: right;
`

const FormFooter = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding-top: 24px;
`

const StyledButton = styled(Button)`
  width: 160px;
`

const CancelButton = styled(StyledButton)`
  margin-right: 16px;
`

function getFriendlyUserStatus(userStatus: UserStatus): string {
  switch (userStatus) {
    case UserStatus.ForceChangePassword:
      return 'Password has not been set'
    case UserStatus.Confirmed:
      return 'Account has been confirmed'
    default:
      return 'Unknown'
  }
}

interface FormData extends UserFormFieldData {}

interface Props {
  projects: Project[]
  user: User
}

export const UpdateAccountUserForm = (props: Props): React.ReactElement => {
  const { projects, user } = props
  const navigate = useNavigate()
  const [updateUser, updateUserState] = useMutation(UPDATE_USER)
  const { refreshData } = useConsoleContext()

  const initialValues = useMemo(() => {
    const formData: FormData = {
      email: user.emailAddress,
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      customerGroups: user.groups.filter((g) => !g.includes('/')),
    }

    for (const project of projects) {
      formData[`projectGroups-${project.id}`] = user.groups.filter((g) =>
        g.startsWith(`${project.id}/`),
      )
    }

    return formData
  }, [user, projects])

  const [handleSubmitState, handleSubmit] = useAsyncFn(
    async (values?: Store) => {
      if (!values) {
        return undefined
      }

      const formData = values as FormData

      const groups = [...(formData.customerGroups ?? [])]
      for (const project of projects) {
        const projectGroups = formData[`projectGroups-${project.id}`]
        groups.push(...(projectGroups ?? []))
      }

      await updateUser({
        variables: {
          username: user.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          groups,
        },
      })

      await refreshData()
      navigate('../account-users')
    },
    [history, updateUser, user, refreshData],
  )

  return (
    <Form initialValues={initialValues} onFinish={handleSubmit}>
      <legend>Account Information</legend>

      <AccountInfoDescriptions bordered column={1}>
        <Descriptions.Item label="Enabled">
          {user.enabled.toString()}
        </Descriptions.Item>
        <Descriptions.Item label="Status">
          {getFriendlyUserStatus(user.status)}{' '}
          {user.status === UserStatus.ForceChangePassword && (
            <DescriptionActions>
              <ResendInvitationButton emailAddress={user.emailAddress} />
            </DescriptionActions>
          )}
        </Descriptions.Item>
      </AccountInfoDescriptions>

      <UserFormFields projects={projects} username={user.username} />

      <FormFeedback showText={!!updateUserState.error}>
        <Alert
          message="There was an error trying to update the user.  Please try again."
          type="error"
        />
      </FormFeedback>

      <FormFooter>
        <Link to="../account-users">
          <CancelButton
            type="default"
            shape="round"
            disabled={handleSubmitState.loading}
          >
            Cancel
          </CancelButton>
        </Link>
        <StyledButton
          loading={handleSubmitState.loading}
          type="primary"
          shape="round"
          htmlType="submit"
        >
          Update User
        </StyledButton>
      </FormFooter>
    </Form>
  )
}
